import { useEffect, useState } from 'react';
import { fetchMedia } from '../api';

// Public photo gallery with three category tabs. Reads image manifests from the
// public media API (anonymous) and renders a responsive grid with a lightbox.
// Images are served directly from Azure Blob Storage.

const TABS = [
  { key: 'cabins', label: 'Cabins', icon: '🏡' },
  { key: 'farm', label: 'Farm', icon: '🌄' },
  { key: 'experiences', label: 'Experiences', icon: '🔥' },
];

export default function PhotoGallery() {
  const [active, setActive] = useState('cabins');
  // cache each category's items so switching tabs is instant after first load
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null); // {url, caption}

  useEffect(() => {
    let cancelled = false;
    if (cache[active]) return; // already loaded
    setLoading(true);
    setError('');
    fetchMedia(active)
      .then((res) => {
        if (cancelled) return;
        setCache((c) => ({ ...c, [active]: res.items || [] }));
      })
      .catch(() => {
        if (!cancelled) setError('Could not load photos right now.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [active, cache]);

  const items = cache[active] || [];

  return (
    <section className="lp-section lp-gallery" id="gallery">
      <h2 className="lp-h2">Photo Gallery</h2>
      <p className="lp-subtle">Explore the cabins, the farm, and the experiences that await.</p>
      <p className="lp-subtle gallery-hint">Tap the category buttons below to switch between cabins, farm, and experiences.</p>

      <div className="gallery-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={active === t.key}
            className={`gallery-tab${active === t.key ? ' is-active' : ''}`}
            onClick={() => setActive(t.key)}
          >
            <span className="lp-icon">{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {loading && <p className="gallery-status">Loading photos…</p>}
      {error && <p className="gallery-status gallery-error">{error}</p>}
      {!loading && !error && items.length === 0 && (
        <p className="gallery-status">Photos coming soon for this section.</p>
      )}

      <div className="gallery-grid">
        {items.map((img) => (
          <figure
            key={img.name}
            className="gallery-item"
            onClick={() => setLightbox(img)}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => { if (e.key === 'Enter') setLightbox(img); }}
          >
            <img src={img.url} alt={img.caption || 'Eagle Ridge Farm'} loading="lazy" />
            {img.caption && <figcaption>{img.caption}</figcaption>}
          </figure>
        ))}
      </div>

      {lightbox && (
        <div className="gallery-lightbox" onClick={() => setLightbox(null)} role="dialog" aria-modal="true">
          <button className="gallery-lightbox-close" aria-label="Close" onClick={() => setLightbox(null)}>×</button>
          <figure onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.caption || 'Eagle Ridge Farm'} />
            {lightbox.caption && <figcaption>{lightbox.caption}</figcaption>}
          </figure>
        </div>
      )}
    </section>
  );
}
